import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { PdfItem } from "./PdfItem";
import { cn } from "../lib/utils";

/**
 * Wraps PdfItem with a drag handle when `draggable` is true.
 * The handle is admin-only and only shown when the unfiltered list is in view.
 */
export function SortablePdfItem({ pdf, view, expanded, onToggleExpand, draggable, compact, renderInlinePreview }) {
  const sortable = useSortable({ id: pdf.id, disabled: !draggable });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  if (!draggable) {
    return (
      <PdfItem
        pdf={pdf}
        view={view}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        compact={compact}
        renderInlinePreview={renderInlinePreview}
      />
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative group",
        isDragging && "z-30 shadow-2xl ring-2 ring-brand-accent/60 rounded-2xl"
      )}
      data-testid={`sortable-${pdf.id}`}
    >
      <button
        type="button"
        aria-label="Drag to reorder"
        title="Drag to reorder"
        className={cn(
          "absolute top-3 right-3 z-20 h-8 w-8 rounded-full",
          "bg-white/90 backdrop-blur border border-slate-200 shadow-sm",
          "flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-white",
          "cursor-grab active:cursor-grabbing touch-none",
          "opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity",
          isDragging && "opacity-100"
        )}
        data-testid={`pdf-drag-${pdf.id}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <PdfItem
        pdf={pdf}
        view={view}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        compact={compact}
        renderInlinePreview={renderInlinePreview}
      />
    </div>
  );
}
